// src/oura/merchant/WorkMarks.tsx
//
// The marks the merchant's work and dates are drawn with (27 Oct 2026), so
// Home and Our work draw them the same way.
//
// A piece of work wears an icon for WHAT it is (a link for "Pages linked
// together"), tinted for where it stands. Jack: a status icon repeated down a
// list says nothing; the bar above already counts the statuses. The tint is
// never the only signal: Done keeps its tick, Needs you its words, and the
// status is read out in words.
//
// A date wears a small calendar tile, month over day.

import {
    BotIcon,
    BracesIcon,
    CalendarCheckIcon,
    CameraIcon,
    CheckIcon,
    ClipboardListIcon,
    EyeIcon,
    FilePlusIcon,
    FileTextIcon,
    GaugeIcon,
    HandshakeIcon,
    LinkIcon,
    ListChecksIcon,
    MapPinIcon,
    PenLineIcon,
    SearchIcon,
    SparklesIcon,
    StethoscopeIcon,
    StoreIcon,
    TagIcon,
    TrophyIcon,
    WrenchIcon,
    type LucideIcon,
} from "lucide-react";
import type { ClientPlanStatus } from "@service/aeo/clientReview";
import { MERCHANT_STATUS } from "@oura/ui";
import { cn } from "../lib/cn";
import { MONTH } from "./workDates";

const ITEM_ICON: Record<string, LucideIcon> = {
    A1: ListChecksIcon,
    A2: BracesIcon,
    A3: BotIcon,
    A4: HandshakeIcon,
    B1: FileTextIcon,
    S1: TagIcon,
    S2: StoreIcon,
    S3: WrenchIcon,
    S4: MapPinIcon,
    S5: SearchIcon,
    S6: LinkIcon,
    S7: GaugeIcon,
    G1: FilePlusIcon,
    G2: StethoscopeIcon,
    G3: TrophyIcon,
    G4: EyeIcon,
    G5: PenLineIcon,
    G6: CameraIcon,
    G7: SparklesIcon,
    G8: CalendarCheckIcon,
};

const TINT: Record<ClientPlanStatus, string> = {
    in_progress: "bg-blue-50 text-blue-600",
    with_google: "bg-violet-50 text-violet-600",
    waiting_on_you: "bg-amber-50 text-amber-600",
    done: "bg-emerald-50 text-emerald-600",
    reported_by_hand: "bg-slate-100 text-slate-600",
    not_started: "bg-gray-100 text-gray-400",
};

/** A piece of work's icon, tinted for its status; Done adds a tick. */
export function ItemIcon({ code, status }: { code: string; status: ClientPlanStatus }) {
    const Icon = ITEM_ICON[code.trim().toUpperCase()] ?? ClipboardListIcon;
    return (
        <span className={cn("relative grid h-7 w-7 shrink-0 place-items-center rounded-lg", TINT[status])}>
            <Icon aria-hidden size={15} />
            {status === "done" && (
                <span aria-hidden className="absolute -bottom-1 -right-1 grid h-3.5 w-3.5 place-items-center rounded-full bg-emerald-600 text-white ring-2 ring-white">
                    <CheckIcon size={9} strokeWidth={3} />
                </span>
            )}
            <span className="sr-only">{MERCHANT_STATUS[status].label}</span>
        </span>
    );
}

/** A calendar tile: the month over the day, centred. The next date is blue. */
export function DateChip({ date, next = false, small = false }: { date: Date | null; next?: boolean; small?: boolean }) {
    return (
        <span
            aria-hidden
            className={cn(
                "flex shrink-0 flex-col items-center justify-center rounded-lg",
                small ? "h-10 w-10 gap-0.5" : "h-12 w-12 gap-1",
                next ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700",
            )}
        >
            <span className={cn("block font-semibold uppercase leading-none tracking-wider", small ? "text-[9px]" : "text-[10px]")}>{date ? MONTH[date.getMonth()] : "Date"}</span>
            <span className={cn("block font-semibold leading-none tabular-nums", small ? "text-base" : "text-lg")}>{date ? date.getDate() : "TBC"}</span>
        </span>
    );
}
